import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Briefcase, FileText, Hammer, Plus, UserCheck, Users } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { canAccessPeopleHiring } from '../utils/employeeBenefitsAccess';
import { userCanReleaseCrewJobs } from '../utils/employeeDesignations';
import {
  getJobApplicationResumeUrl,
  subscribeJobApplications,
  subscribeJobPostings,
} from '../utils/hiringFirestore';
import { subscribeJobOfferLetters } from '../utils/jobOfferLettersFirestore';
import { subscribeContractorApplicants } from '../utils/contractorOnboardFirestore';
import { subscribePublishedHandbookPolicies } from '../utils/handbookPoliciesFirestore';
import {
  candidateFinishedHiring,
  hiredSeatForOffer,
  latestOfferByApplicationId,
} from '../utils/jobOfferLetter';
import { subscribeCrewJobs } from '../utils/crewJobsFirestore';
import type { CrewJob } from '../types/crewJobs';
import {
  buildHiringResumeDocumentId,
  HIRING_VIEWER_LEAD_ID,
  isHiringResumePdf,
} from '../utils/hiringResumeViewer';
import {
  CONTRACTOR_ONBOARD_VIEWER_LEAD_ID,
  type ContractorOnboardViewerOpen,
} from '../utils/contractorOnboardViewer';
import type { JobApplication, JobOfferLetter, JobPosting } from '../types/hiring';
import type { ContractorApplicant } from '../types/contractorOnboard';
import HiredRoster, { type HiredRosterRow } from '../components/people/hiring/HiredRoster';
import JobPostingEditor from '../components/people/hiring/JobPostingEditor';
import JobApplicationsPanel from '../components/people/hiring/JobApplicationsPanel';
import CrewJobsPanel from '../components/people/hiring/CrewJobsPanel';
import CrewJobEditor from '../components/people/hiring/CrewJobEditor';
import type { HiringResumeViewerOpen } from '../components/people/hiring/JobApplicationDetail';
import '../components/pm/pm-shared.css';
import './OperationsHub.css';
import './PeopleHubPage.css';
import './PeopleHiringPage.css';

const PdfViewerModal = lazy(() => import('../components/pdf/PdfViewerModal'));
const ImageViewerModal = lazy(() => import('../components/pdf/ImageViewerModal'));

type HiringResumeViewerState = HiringResumeViewerOpen & {
  annotationId?: string;
  pageNumber?: number;
};

const PeopleHiringPage: React.FC = () => {
  const { user } = useAuth();
  const { crmUsers, timberProjects } = useCRM();
  const [searchParams, setSearchParams] = useSearchParams();
  const [postings, setPostings] = useState<JobPosting[]>([]);
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPosting, setEditingPosting] = useState<JobPosting | null>(null);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const [applicationFilterPostingId, setApplicationFilterPostingId] = useState<string>('');
  const [resumeViewer, setResumeViewer] = useState<HiringResumeViewerState | null>(null);
  const resumeDeepLinkKeyRef = useRef<string | null>(null);
  const [packetViewer, setPacketViewer] = useState<ContractorOnboardViewerOpen | null>(null);
  const [crewJobs, setCrewJobs] = useState<CrewJob[]>([]);
  const [selectedCrewJobId, setSelectedCrewJobId] = useState<string | null>(null);
  const [selectedContractorApplicantId, setSelectedContractorApplicantId] = useState<string | null>(null);
  const [crewEditorOpen, setCrewEditorOpen] = useState(false);
  const [editingCrewJob, setEditingCrewJob] = useState<CrewJob | null>(null);
  const [offers, setOffers] = useState<JobOfferLetter[]>([]);
  const [contractorApplicants, setContractorApplicants] = useState<ContractorApplicant[]>([]);
  const [requiredPolicyCount, setRequiredPolicyCount] = useState(0);
  const [policiesReady, setPoliciesReady] = useState(false);

  const prefillPayrollPositionId = searchParams.get('payrollPositionId') || undefined;
  const prefillTitle = searchParams.get('title') || undefined;
  const prefillDepartment = searchParams.get('department') || undefined;

  useEffect(() => {
    if (!user || !canAccessPeopleHiring(user.role, user.designations)) return;
    return subscribeJobPostings(setPostings);
  }, [user]);

  useEffect(() => {
    if (!user || !canAccessPeopleHiring(user.role, user.designations)) return;
    return subscribeCrewJobs(setCrewJobs);
  }, [user]);

  useEffect(() => {
    if (!user || !canAccessPeopleHiring(user.role, user.designations)) return;
    return subscribeJobOfferLetters(setOffers);
  }, [user]);

  useEffect(() => {
    if (!user || !canAccessPeopleHiring(user.role, user.designations)) return;
    return subscribeContractorApplicants(setContractorApplicants);
  }, [user]);

  useEffect(() => {
    if (!user || !canAccessPeopleHiring(user.role, user.designations)) return;
    return subscribePublishedHandbookPolicies(
      list => {
        setRequiredPolicyCount(list.length);
        setPoliciesReady(true);
      },
      () => {
        setRequiredPolicyCount(0);
        setPoliciesReady(true);
      },
      { userId: user.id },
    );
  }, [user]);

  useEffect(() => {
    if (!user || !canAccessPeopleHiring(user.role, user.designations)) return;
    return subscribeJobApplications(
      {
        jobPostingId: applicationFilterPostingId || undefined,
      },
      setApplications,
    );
  }, [user, applicationFilterPostingId]);

  const tabParam = searchParams.get('tab');
  const hiringTab: 'careers' | 'candidates' | 'crew' | 'employees' | 'contractors' =
    tabParam === 'crew'
      ? 'crew'
      : tabParam === 'employees'
        ? 'employees'
        : tabParam === 'contractors'
          ? 'contractors'
          : tabParam === 'candidates' || searchParams.get('application')
            ? 'candidates'
            : 'careers';
  const teamCommentId = searchParams.get('teamComment');

  useEffect(() => {
    const applicationId = searchParams.get('application');
    if (applicationId) setSelectedApplicationId(applicationId);
    const crewJobId = searchParams.get('job');
    if (crewJobId) setSelectedCrewJobId(crewJobId);
    const applicantId = searchParams.get('applicant');
    if (applicantId) setSelectedContractorApplicantId(applicantId);
  }, [searchParams]);

  useEffect(() => {
    const applicationId = searchParams.get('application');
    if (!applicationId || applications.length === 0) return;
    if (applications.some(row => row.id === applicationId)) return;
    setApplicationFilterPostingId('');
  }, [applications, searchParams]);

  useEffect(() => {
    if (searchParams.get('new') === '1' || prefillPayrollPositionId) {
      setEditingPosting(null);
      setEditorOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, [prefillPayrollPositionId, searchParams, setSearchParams]);

  const closeResumeViewer = useCallback(() => {
    setResumeViewer(null);
    const next = new URLSearchParams(searchParams);
    next.delete('resume');
    next.delete('comment');
    next.delete('page');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openPacketDoc = useCallback((doc: ContractorOnboardViewerOpen) => {
    setResumeViewer(null);
    setPacketViewer(doc);
  }, []);

  const closePacketViewer = useCallback(() => {
    setPacketViewer(null);
  }, []);

  useEffect(() => {
    const applicationId = searchParams.get('application');
    const comment = searchParams.get('comment') || undefined;
    const pageRaw = searchParams.get('page');
    const pageNumber = pageRaw ? Number(pageRaw) : undefined;
    const wantResume = searchParams.get('resume') === '1' || !!comment;
    if (!applicationId || !wantResume) return;

    const application = applications.find(row => row.id === applicationId);
    if (!application || !isHiringResumePdf(application.resume) || !application.resume.storagePath) return;

    const deepLinkKey = `${applicationId}:${comment || ''}:${pageRaw || ''}`;
    if (resumeDeepLinkKeyRef.current === deepLinkKey) return;
    resumeDeepLinkKeyRef.current = deepLinkKey;

    void getJobApplicationResumeUrl(application.resume.storagePath).then(url => {
      setResumeViewer({
        url,
        title: `${application.firstName} ${application.lastName} — ${application.resume.fileName}`,
        documentId: buildHiringResumeDocumentId(application.id),
        leadName: `${application.firstName} ${application.lastName}`.trim() || undefined,
        annotationId: comment,
        pageNumber: pageNumber != null && !Number.isNaN(pageNumber) ? pageNumber : undefined,
      });
    }).catch(() => {
      resumeDeepLinkKeyRef.current = null;
    });
  }, [applications, searchParams]);

  useEffect(() => {
    const wantResume = searchParams.get('resume') === '1' || !!searchParams.get('comment');
    if (!wantResume) setResumeViewer(null);
  }, [searchParams]);

  const offerByApplicationId = useMemo(
    () => latestOfferByApplicationId(offers),
    [offers],
  );

  const finishedHires = useMemo(() => {
    if (!policiesReady) return [];
    const rows: Array<{ application: JobApplication; offer: JobOfferLetter; seat: 'employee' | 'contractor' }> = [];
    for (const application of applications) {
      const offer = offerByApplicationId.get(application.id) || null;
      if (!candidateFinishedHiring({ application, offer, requiredPolicyCount })) continue;
      if (!offer) continue;
      rows.push({ application, offer, seat: hiredSeatForOffer(offer) });
    }
    return rows;
  }, [applications, offerByApplicationId, policiesReady, requiredPolicyCount]);

  const hiredApplicationIds = useMemo(
    () => new Set(finishedHires.map(row => row.application.id)),
    [finishedHires],
  );

  const employeeRows = useMemo<HiredRosterRow[]>(
    () => finishedHires
      .filter(row => row.seat === 'employee')
      .map(row => ({ kind: 'offer', id: row.application.id, application: row.application, offer: row.offer })),
    [finishedHires],
  );

  const contractorRows = useMemo<HiredRosterRow[]>(() => {
    const fromOffers: HiredRosterRow[] = finishedHires
      .filter(row => row.seat === 'contractor')
      .map(row => ({ kind: 'offer', id: row.application.id, application: row.application, offer: row.offer }));
    const fromCrew: HiredRosterRow[] = contractorApplicants
      .filter(applicant => applicant.status === 'pooled')
      .map(applicant => ({ kind: 'contractor', id: applicant.id, applicant }));
    return [...fromOffers, ...fromCrew];
  }, [contractorApplicants, finishedHires]);

  const newCount = useMemo(
    () => applications.filter(a => a.status === 'new' && !hiredApplicationIds.has(a.id)).length,
    [applications, hiredApplicationIds],
  );

  const candidateCount = useMemo(
    () => applications.filter(a => a.status !== 'rejected' && !hiredApplicationIds.has(a.id)).length,
    [applications, hiredApplicationIds],
  );

  useEffect(() => {
    const applicationId = searchParams.get('application');
    if (!applicationId || !hiredApplicationIds.has(applicationId)) return;
    const hire = finishedHires.find(row => row.application.id === applicationId);
    if (!hire) return;
    const want = hire.seat === 'employee' ? 'employees' : 'contractors';
    if (searchParams.get('tab') === want) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', want);
    setSearchParams(next, { replace: true });
  }, [finishedHires, hiredApplicationIds, searchParams, setSearchParams]);

  const consumeTeamComment = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('teamComment');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openResumePin = useCallback((applicationId: string, opts: { annotationId: string; pageNumber?: number }) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'candidates');
    next.set('application', applicationId);
    next.set('resume', '1');
    next.set('comment', opts.annotationId);
    if (opts.pageNumber != null && !Number.isNaN(opts.pageNumber)) {
      next.set('page', String(opts.pageNumber));
    } else {
      next.delete('page');
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  if (!user || !canAccessPeopleHiring(user.role, user.designations)) return null;

  const canReleaseCrew = userCanReleaseCrewJobs(user, crmUsers);

  const setHiringTab = (tab: 'careers' | 'candidates' | 'crew' | 'employees' | 'contractors') => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'crew') {
      next.set('tab', 'crew');
      next.delete('application');
      next.delete('resume');
      next.delete('comment');
      next.delete('page');
      next.delete('teamComment');
    } else if (tab === 'candidates') {
      next.set('tab', 'candidates');
      next.delete('job');
      next.delete('applicant');
      const applicationId = searchParams.get('application');
      if (applicationId && hiredApplicationIds.has(applicationId)) next.delete('application');
    } else if (tab === 'employees' || tab === 'contractors') {
      next.set('tab', tab);
      next.delete('job');
      next.delete('resume');
      next.delete('comment');
      next.delete('page');
      next.delete('teamComment');
      const applicationId = searchParams.get('application');
      const hire = applicationId
        ? finishedHires.find(row => row.application.id === applicationId)
        : undefined;
      const belongs = Boolean(
        hire &&
        ((tab === 'employees' && hire.seat === 'employee') ||
          (tab === 'contractors' && hire.seat === 'contractor')),
      );
      if (!belongs) next.delete('application');
      if (tab === 'employees' || !belongs) next.delete('applicant');
    } else {
      next.delete('tab');
      next.delete('job');
      next.delete('applicant');
      next.delete('application');
      next.delete('resume');
      next.delete('comment');
      next.delete('page');
      next.delete('teamComment');
    }
    setSearchParams(next, { replace: true });
  };

  const handleSelectCrewJobId = (id: string | null) => {
    setSelectedCrewJobId(id);
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'crew');
    if (id) next.set('job', id);
    else next.delete('job');
    setSearchParams(next, { replace: true });
  };

  const handleSelectApplicantId = (id: string | null) => {
    setSelectedContractorApplicantId(id);
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'crew');
    if (id) next.set('applicant', id);
    else next.delete('applicant');
    setSearchParams(next, { replace: true });
  };

  const openCreate = () => {
    if (hiringTab === 'crew') {
      setEditingCrewJob(null);
      setCrewEditorOpen(true);
      return;
    }
    if (hiringTab === 'candidates') return;
    setEditingPosting(null);
    setEditorOpen(true);
  };

  const openEdit = (posting: JobPosting) => {
    setEditingPosting(posting);
    setEditorOpen(true);
  };

  const handleSelectApplicationId = (id: string | null) => {
    setSelectedApplicationId(id);
    const next = new URLSearchParams(searchParams);
    const hire = id ? finishedHires.find(row => row.application.id === id) : undefined;
    next.set('tab', hire ? (hire.seat === 'employee' ? 'employees' : 'contractors') : 'candidates');
    if (id) next.set('application', id);
    else next.delete('application');
    if (hire) next.delete('applicant');
    next.delete('resume');
    next.delete('comment');
    next.delete('page');
    next.delete('teamComment');
    setSearchParams(next, { replace: true });
  };

  const handleSelectHiredId = (id: string | null) => {
    if (!id) {
      setSelectedApplicationId(null);
      setSelectedContractorApplicantId(null);
      const next = new URLSearchParams(searchParams);
      next.delete('application');
      next.delete('applicant');
      setSearchParams(next, { replace: true });
      return;
    }
    if (finishedHires.some(row => row.application.id === id)) {
      handleSelectApplicationId(id);
      return;
    }
    setSelectedApplicationId(null);
    setSelectedContractorApplicantId(id);
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'contractors');
    next.delete('application');
    next.delete('job');
    next.set('applicant', id);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="people-hub-route-root people-hiring-root">
      <aside className="people-hiring-sidebar" aria-label="People sections">
        <div className="people-hiring-sidebar-header">
          <h3 className="people-hiring-sidebar-title">Hired</h3>
        </div>
        <button
          type="button"
          className={`people-hiring-nav ${hiringTab === 'employees' ? 'active' : ''}`}
          aria-current={hiringTab === 'employees' ? 'page' : undefined}
          onClick={() => setHiringTab('employees')}
        >
          <UserCheck size={14} aria-hidden />
          <span className="people-hiring-nav-label">Employees</span>
          <span className="people-hiring-nav-count">{employeeRows.length}</span>
        </button>
        <button
          type="button"
          className={`people-hiring-nav ${hiringTab === 'contractors' ? 'active' : ''}`}
          aria-current={hiringTab === 'contractors' ? 'page' : undefined}
          onClick={() => setHiringTab('contractors')}
        >
          <FileText size={14} aria-hidden />
          <span className="people-hiring-nav-label">1099s</span>
          <span className="people-hiring-nav-count">{contractorRows.length}</span>
        </button>
        <div className="people-hiring-sidebar-header">
          <h3 className="people-hiring-sidebar-title">Hiring</h3>
        </div>
        <button
          type="button"
          className={`people-hiring-nav ${hiringTab === 'careers' ? 'active' : ''}`}
          aria-current={hiringTab === 'careers' ? 'page' : undefined}
          onClick={() => setHiringTab('careers')}
        >
          <Briefcase size={14} aria-hidden />
          <span className="people-hiring-nav-label">Careers</span>
          <span className="people-hiring-nav-count">{postings.length}</span>
        </button>
        <button
          type="button"
          className={`people-hiring-nav ${hiringTab === 'candidates' ? 'active' : ''}`}
          aria-current={hiringTab === 'candidates' ? 'page' : undefined}
          onClick={() => setHiringTab('candidates')}
        >
          <Users size={14} aria-hidden />
          <span className="people-hiring-nav-label">Candidates</span>
          <span className="people-hiring-nav-count">
            {newCount > 0 ? `${newCount} new` : candidateCount}
          </span>
        </button>
        <button
          type="button"
          className={`people-hiring-nav ${hiringTab === 'crew' ? 'active' : ''}`}
          aria-current={hiringTab === 'crew' ? 'page' : undefined}
          onClick={() => setHiringTab('crew')}
        >
          <Hammer size={14} aria-hidden />
          <span className="people-hiring-nav-label">Crew jobs</span>
          <span className="people-hiring-nav-count">{crewJobs.length}</span>
        </button>
      </aside>

      <div className="people-hiring-main">
      <div className="ops-hub-toolbar">
        <div className="ops-hub-toolbar-left">
          <div>
            <h2 className="ops-hub-title">
              {hiringTab === 'crew'
                ? 'Crew jobs'
                : hiringTab === 'employees'
                  ? 'Employees'
                  : hiringTab === 'contractors'
                    ? '1099s'
                    : hiringTab === 'candidates'
                      ? 'Candidates'
                      : 'Careers'}
            </h2>
            <p className="people-hub-subtitle">
              {hiringTab === 'crew'
                ? 'Select a raise, then work each person through 1099 onboarding before they go on the roster.'
                : hiringTab === 'employees'
                  ? 'People who signed an employment agreement and finished hiring. Open someone to manage their contract and benefits.'
                  : hiringTab === 'contractors'
                    ? '1099s who finished their agreement. Open someone to see the contract and pay.'
                    : hiringTab === 'candidates'
                      ? 'Review applications and discuss candidates with the team.'
                      : 'Manage job postings for the careers page.'}
            </p>
          </div>
        </div>
        {(hiringTab === 'careers' || hiringTab === 'crew') && (
          <div className="ops-hub-toolbar-right">
            <button type="button" className="ops-new-btn" onClick={openCreate}>
              <Plus size={14} /> {hiringTab === 'crew' ? 'New crew job' : 'New posting'}
            </button>
          </div>
        )}
      </div>

      <div className="people-hub-body pm-scroll">
        {hiringTab === 'employees' || hiringTab === 'contractors' ? (
          <HiredRoster
            seat={hiringTab === 'employees' ? 'employee' : 'contractor'}
            rows={hiringTab === 'employees' ? employeeRows : contractorRows}
            postings={postings}
            crewJobs={crewJobs}
            selectedId={
              hiringTab === 'employees'
                ? selectedApplicationId
                : (selectedApplicationId && contractorRows.some(row => row.kind === 'offer' && row.id === selectedApplicationId)
                  ? selectedApplicationId
                  : selectedContractorApplicantId)
            }
            onSelectId={handleSelectHiredId}
            reviewerUid={user.id}
            onOpenPacketDoc={openPacketDoc}
          />
        ) : hiringTab === 'crew' ? (
          <CrewJobsPanel
              jobs={crewJobs}
              selectedJobId={selectedCrewJobId}
              onSelectJobId={handleSelectCrewJobId}
              selectedApplicantId={selectedContractorApplicantId}
              onSelectApplicantId={handleSelectApplicantId}
              onEditJob={job => {
                setEditingCrewJob(job);
                setCrewEditorOpen(true);
              }}
              canRelease={canReleaseCrew}
              reviewerUid={user.id}
              onOpenPacketDoc={openPacketDoc}
            />
        ) : hiringTab === 'candidates' ? (
          <JobApplicationsPanel
            applications={applications}
            postings={postings}
            newCount={newCount}
            filterPostingId={applicationFilterPostingId}
            onFilterPostingIdChange={setApplicationFilterPostingId}
            selectedApplicationId={selectedApplicationId}
            onSelectApplicationId={handleSelectApplicationId}
            reviewerUid={user.id}
            highlightTeamCommentId={teamCommentId}
            onTeamCommentConsumed={consumeTeamComment}
            highlightPdfCommentId={searchParams.get('comment')}
            excludeApplicationIds={hiredApplicationIds}
            onOpenResumePin={opts => {
              if (!selectedApplicationId) return;
              openResumePin(selectedApplicationId, opts);
            }}
          />
        ) : (
        <section className="people-hub-panel people-hiring-postings">
          <div className="people-hiring-section-head">
            <h3>Job postings</h3>
            <span className="people-hiring-muted">{postings.length} total</span>
          </div>
          {postings.length === 0 ? (
            <div className="people-hiring-empty">
              <Briefcase size={28} />
              <p>No job postings yet.</p>
            </div>
          ) : (
            <div className="people-hiring-table-wrap">
              <table className="people-hiring-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Apps</th>
                    <th>Published</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {postings.map(p => (
                    <tr key={p.id}>
                      <td>
                        <strong>{p.title}</strong>
                        <div className="people-hiring-muted">{p.location || '—'}</div>
                      </td>
                      <td>
                        <span className={`people-hiring-status people-hiring-status--${p.status}`}>
                          {p.status}
                        </span>
                      </td>
                      <td>{p.applicationCount}</td>
                      <td>
                        {p.publishedAt
                          ? p.publishedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : '—'}
                      </td>
                      <td>
                        <button type="button" className="pm-btn pm-btn-ghost" onClick={() => openEdit(p)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        )}
      </div>
      </div>

      {resumeViewer && (
        <Suspense fallback={null}>
          <PdfViewerModal
            key={`${resumeViewer.documentId}:${resumeViewer.annotationId ?? ''}:${resumeViewer.pageNumber ?? ''}`}
            url={resumeViewer.url}
            title={resumeViewer.title}
            documentId={resumeViewer.documentId}
            leadId={HIRING_VIEWER_LEAD_ID}
            leadName={resumeViewer.leadName}
            disableTakeoff
            initialAnnotationId={resumeViewer.annotationId}
            initialPageNumber={resumeViewer.pageNumber}
            onClose={closeResumeViewer}
          />
        </Suspense>
      )}

      {packetViewer?.kind === 'pdf' && (
        <Suspense fallback={null}>
          <PdfViewerModal
            key={packetViewer.documentId}
            url={packetViewer.url}
            title={packetViewer.title}
            documentId={packetViewer.documentId}
            leadId={CONTRACTOR_ONBOARD_VIEWER_LEAD_ID}
            leadName={packetViewer.leadName}
            disableTakeoff
            onClose={closePacketViewer}
          />
        </Suspense>
      )}

      {packetViewer?.kind === 'image' && (
        <Suspense fallback={null}>
          <ImageViewerModal
            key={packetViewer.documentId}
            url={packetViewer.url}
            title={packetViewer.title}
            documentId={packetViewer.documentId}
            leadId={CONTRACTOR_ONBOARD_VIEWER_LEAD_ID}
            leadName={packetViewer.leadName}
            mimeType={packetViewer.mimeType}
            onClose={closePacketViewer}
          />
        </Suspense>
      )}

      {crewEditorOpen && (
        <CrewJobEditor
          job={editingCrewJob}
          timberProjects={timberProjects}
          onClose={() => {
            setCrewEditorOpen(false);
            setEditingCrewJob(null);
          }}
        />
      )}

      {editorOpen && (
        <JobPostingEditor
          posting={editingPosting}
          prefill={{
            payrollPositionId: prefillPayrollPositionId,
            title: prefillTitle,
            department: prefillDepartment,
          }}
          onClose={() => {
            setEditorOpen(false);
            setEditingPosting(null);
          }}
        />
      )}
    </div>
  );
};

export default PeopleHiringPage;
